import React from 'react';
import { motion } from 'framer-motion';
import { Layout, PenTool, Sparkles, ArrowRight } from 'lucide-react';

const steps = [
    {
        num: "01",
        title: "Mind Map & Freeform",
        desc: "Start with an infinite canvas. Brainstorm ideas, drop in images, and sketch structures freely.",
        icon: Layout,
        color: "text-blue-400",
        bg: "bg-blue-500/10",
        border: "border-blue-500/20"
    },
    {
        num: "02",
        title: "Connect & Organize",
        desc: "Link related concepts visually. Build a structured knowledge graph that mirrors your thinking.",
        icon: PenTool,
        color: "text-pink-400",
        bg: "bg-pink-500/10",
        border: "border-pink-500/20"
    },
    {
        num: "03",
        title: "AI Synthesis",
        desc: "Ask Gemini to review your board. Generate study guides, quizzes, and summaries instantly.",
        icon: Sparkles,
        color: "text-amber-400",
        bg: "bg-amber-500/10",
        border: "border-amber-500/20"
    }
];

export const HowItWorksSection: React.FC = () => {
    return (
        <section id="how-it-works" className="py-32 px-4 relative overflow-hidden bg-[#0a0a0f]">
            {/* Background Gradients (Neutral) */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-white/[0.02] blur-[120px] rounded-full pointer-events-none" />

            <div className="max-w-7xl mx-auto relative z-10">
                <div className="text-center mb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-sm text-blue-200 mb-6"
                    >
                        <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                        Workflow
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight"
                    >
                        From Chaos to <span className="text-zinc-500">Clarity</span>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="text-zinc-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed"
                    >
                        Three simple steps to transform your scattered thoughts into a structured knowledge base.
                    </motion.p>
                </div>

                <div className="relative grid md:grid-cols-3 gap-8">
                    {/* Connection Line Desktop */}
                    <div className="hidden md:block absolute top-24 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-white/10 to-transparent z-0">
                        <motion.div
                            className="absolute top-0 left-0 h-full w-24 bg-gradient-to-r from-transparent via-white/50 to-transparent blur-[1px]"
                            animate={{ x: ["-100%", "900%"] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                        />
                    </div>

                    {steps.map((step, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.2 }}
                            className="relative group"
                        >
                            {/* Card */}
                            <div className="h-full p-8 rounded-2xl bg-[#0a0a0f] border border-white/5 hover:border-white/20 transition-colors duration-300 relative z-10 overflow-hidden">
                                {/* Hover Glow */}
                                <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                                {/* Step Number */}
                                <div className="absolute top-6 right-8 text-6xl font-bold text-white/[0.02] group-hover:text-white/[0.06] transition-colors duration-300">
                                    {step.num}
                                </div>

                                {/* Icon */}
                                <div className={`w-14 h-14 rounded-xl ${step.bg} border ${step.border} flex items-center justify-center mb-8 relative group-hover:scale-105 transition-transform duration-300`}>
                                    <step.icon className={`w-7 h-7 ${step.color}`} />
                                </div>

                                {/* Content */}
                                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                                    {step.title}
                                    <ArrowRight className="w-4 h-4 text-zinc-500 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 group-hover:text-white" />
                                </h3>
                                <p className="text-zinc-400 leading-relaxed">
                                    {step.desc}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
